import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import type { AppConfig } from '../config';

import {
  type CandidateService,
  createCandidateService,
} from './candidateService/candidateService';
import { createCandidateServiceMock } from './candidateService/candidateService.mock';
import {
  type CompanyDetailsService,
  createCompanyDetailsService,
} from './companyDetailsService/companyDetailsService';
import { createCompanyDetailsServiceMock } from './companyDetailsService/companyDetailsService.mock';
import {
  type CompanyProfileService,
  createCompanyProfileService,
} from './companyProfileService/companyProfileService';
import { createCompanyProfileServiceMock } from './companyProfileService/companyProfileService.mock';
import {
  type CompanyReviewService,
  createCompanyReviewService,
} from './companyReviewService/companyReviewService';
import { createCompanyReviewServiceMock } from './companyReviewService/companyReviewService.mock';
import { createLegacyCompanyProfileService } from './legacyCompanyProfileService/legacyCompanyProfileService';
import { createLegacyCompanyProfileServiceMock } from './legacyCompanyProfileService/legacyCompanyProfileService.mock';
import {
  createLocationService,
  type LocationService,
} from './locationService/locationService';
import { createLocationServiceMock } from './locationService/locationService.mock';
import { createRoleService, type RoleService } from './roleService/roleService';
import { createRoleServiceMock } from './roleService/roleService.mock';
import {
  createSitemapService,
  type SitemapService,
} from './sitemapService/sitemapService';
import { createSitemapServiceMock } from './sitemapService/sitemapService.mock';
import type { Logger } from '@seek/logger';

export interface Services {
  candidateService: CandidateService;
  companyDetailsService: CompanyDetailsService;
  companyProfileService: CompanyProfileService;
  companyReviewService: CompanyReviewService;
  legacyCompanyProfileService: ReturnType<
    typeof createLegacyCompanyProfileService
  >;
  locationService: LocationService;
  roleService: RoleService;
  sitemapService: SitemapService;
}

export const createServices = ({
  client,
  config,
  logger,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  config: AppConfig;
  logger: Logger;
}): Services => {
  if (config.environment === 'local') {
    return {
      companyProfileService: createCompanyProfileServiceMock({ config }),
      companyReviewService: createCompanyReviewServiceMock(),
      candidateService: createCandidateServiceMock(),
      roleService: createRoleServiceMock(),
      legacyCompanyProfileService: createLegacyCompanyProfileServiceMock(),
      sitemapService: createSitemapServiceMock({ config }),
      locationService: createLocationServiceMock(),
      companyDetailsService: createCompanyDetailsServiceMock({ config }),
    };
  }

  if (config.environment === 'development') {
    return {
      companyProfileService: createCompanyProfileService({
        client,
        config,
        logger,
      }),
      companyReviewService: createCompanyReviewService({ client, logger }),
      candidateService: createCandidateService({ client, config }),
      roleService: createRoleService({ client, config }),
      legacyCompanyProfileService: createLegacyCompanyProfileService({
        companyProfilesApiBaseUrl: config.companyProfilesApiBaseUrl,
        config,
        logger,
      }),
      sitemapService: createSitemapServiceMock({ config }),
      locationService: createLocationService({ client, config }),
      companyDetailsService: createCompanyDetailsService({
        client,
        config,
        logger,
      }),
    };
  }

  return {
    companyProfileService: createCompanyProfileService({
      client,
      config,
      logger,
    }),
    companyReviewService: createCompanyReviewService({ client, logger }),
    candidateService: createCandidateService({ client, config }),
    roleService: createRoleService({ client, config }),
    legacyCompanyProfileService: createLegacyCompanyProfileService({
      companyProfilesApiBaseUrl: config.companyProfilesApiBaseUrl,
      config,
      logger,
    }),
    sitemapService: createSitemapService({ config }),
    locationService: createLocationService({ client, config }),
    companyDetailsService: createCompanyDetailsService({
      client,
      config,
      logger,
    }),
  };
};

export {
  createLocationSlugService,
  type LocationDetails,
  type LocationSlugService,
} from './locationService/locationSlugService';
